import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Car,
  MapPin,
  MapPinned,
  Route,
  Smartphone,
  TrendingUp,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { categoryTone, categoryToneClass } from "@/data/availableRidesData";
import {
  APP_STORE_URL,
  DURI_APP_URL,
  PLAY_STORE_URL,
  type GuestCardData,
} from "@/data/waitingGuestsData";
import { getBrowserLocation, getCityStateFromLatLng } from "@/utils/geoLocationUtils";
import { LOCATION_UNKNOWN_DISPLAY } from "@/utils/locationDisplay";
import { ROUTE_PATHS } from "@/routes/routePaths";
import { MarketingRideSearchBar } from "@/components/common/MarketingRideSearchBar";
import {
  getGuestSummary,
  getWaitingRoutes,
  type GuestSummaryResponse,
  type WaitingRoutesResponse,
} from "@/services/waitingGuestsService";
import { getHostWaitingRoutes } from "@/services/availableHostsService";

function GuestCard({ guest }: { guest: GuestCardData }) {
  const tone = guest.tripPurpose ? categoryTone(guest.tripPurpose) : "slate";
  return (
    <article className="group rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{guest.date}</span>
        <span className="text-sm font-medium text-muted-foreground">
          {guest.timeWindow}
        </span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
          {guest.seatsNeeded} seat{guest.seatsNeeded === 1 ? "" : "s"} needed
        </span>
        {guest.tripPurpose && (
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[10px] font-semibold",
              categoryToneClass[tone]
            )}
          >
            {guest.tripPurpose}
          </span>
        )}
        {guest.verified && (
          <span className="flex items-center gap-1 text-xs text-accent">
            <BadgeCheck className="h-3.5 w-3.5" />
            Verified
          </span>
        )}
      </div>
      {guest.note && (
        <p className="mb-2 text-xs text-muted-foreground">&ldquo;{guest.note}&rdquo;</p>
      )}
     
      <a
        href={DURI_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2 text-sm font-semibold text-accent-foreground opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
      >
        Offer ride in App <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </article>
  );
}

export function WaitingGuestsPageContent() {
  const [fromCity, setFromCity] = useState("");
  const [showAllCities, setShowAllCities] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isRoutesLoading, setIsRoutesLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [summary, setSummary] = useState<GuestSummaryResponse | null>(null);
  const [waitingRoutesResponse, setWaitingRoutesResponse] = useState<WaitingRoutesResponse | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      setIsSummaryLoading(true);
      setIsRoutesLoading(true);
      setErrorMessage("");
      try {
        let resolvedCity = "";
        try {
          const { lat, lng } = await getBrowserLocation({ timeout: 5000 });
          const geo = await getCityStateFromLatLng(lat, lng);
          if (geo.city && geo.city !== "Not detected") {
            resolvedCity = geo.city;
          }
        } catch {
          // Leave empty: summary API can be called without `location`
        }

        if (cancelled) return;
        setFromCity(resolvedCity);
        const data = await getGuestSummary(resolvedCity || undefined);
        if (!cancelled) {
          setSummary(data);
        }

        const initialRoutes = await getWaitingRoutes({
          fromCity: resolvedCity || undefined,
          page: 1,
        });
        if (!cancelled) {
          setWaitingRoutesResponse(initialRoutes);
        }

        await getHostWaitingRoutes({
          fromCity: resolvedCity || undefined,
          page: 1,
        });
      } catch {
        if (!cancelled) {
          setErrorMessage("Unable to load ride requests summary right now.");
        }
      } finally {
        if (!cancelled) {
          setIsSummaryLoading(false);
          setIsRoutesLoading(false);
        }
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, []);

  const routeSections = useMemo(() => {
    if (!waitingRoutesResponse) return [];
    return waitingRoutesResponse.routes.map((route, routeIndex) => ({
      id: `${route.from}-${route.to}-${routeIndex}`,
      from: route.from,
      to: route.to,
      waitingCount: route.count,
      guests: route.cards.map((card, cardIndex) => ({
        id: `${route.from}-${route.to}-${cardIndex}`,
        date: card.date,
        timeWindow: card.time,
        seatsNeeded: card.seatsNeeded,
        tripPurpose: undefined,
      })),
    }));
  }, [waitingRoutesResponse]);

  const visibleGuestCount = useMemo(() => {
    return routeSections.reduce((acc, s) => acc + s.guests.length, 0);
  }, [routeSections]);

  const popularRoutes = summary?.mostPopularRoutes ?? [];
  const otherRoutes = waitingRoutesResponse?.otherRoutes ?? [];

  const departureLabel = showAllCities ? "India" : fromCity.trim() || LOCATION_UNKNOWN_DISPLAY;

  const handleShowAllCitiesToggle = async () => {
    const nextValue = !showAllCities;
    setShowAllCities(nextValue);
    setIsSummaryLoading(true);
    setErrorMessage("");
    try {
      const data = await getGuestSummary(
        nextValue ? undefined : fromCity.trim() || undefined
      );
      setSummary(data);
    } catch {
      setErrorMessage("Unable to refresh summary for selected location.");
    } finally {
      setIsSummaryLoading(false);
    }
  };

  return (
    <div className="waiting-guests-page min-h-screen bg-background text-foreground antialiased">
      <section className="bg-waiting-hero px-4 py-16 text-primary-foreground sm:py-24">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/20 px-4 py-1.5 text-sm font-medium shadow-sm backdrop-blur-sm">
            <Users className="h-4 w-4" /> For Hosts — Ride Requests
          </div>
          <h1 className="mb-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            Ride Requests
          </h1>
          <p className="mb-8 text-lg text-primary-foreground/90">
            See demand on your route before you drive. {summary?.totalGuestsWaiting ?? 0}{" "}
            intercity requests across India — offer a seat and split costs fairly.
          </p>
        </div>
      </section>

      <main className="container py-8">
        <div className="mb-6 rounded-xl border border-accent/25 bg-accent/[0.07] p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <MapPinned className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <p className="text-base font-semibold text-foreground">
                  👋 Hosts near {fromCity.trim() || LOCATION_UNKNOWN_DISPLAY}
                </p>
                <p className="text-sm text-muted-foreground">
                  {showAllCities
                    ? "Browse ride requests across India. Filter below by route and date."
                    : `These guests need rides starting from ${
                        fromCity.trim() || LOCATION_UNKNOWN_DISPLAY
                      }. Pick a departure city in the search row if needed.`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void handleShowAllCitiesToggle()}
              className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card"
            >
              {showAllCities ? "Focus one city" : "Show all cities"}
            </button>
          </div>
        </div>

        <div className="mb-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-2 flex items-center gap-2 text-accent">
                <Users className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">
                  Ride requests
                </span>
              </div>
              <p className="text-3xl font-extrabold text-foreground">
                {isSummaryLoading ? "..." : summary?.totalGuestsWaiting ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-2 flex items-center gap-2 text-accent">
                <Route className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">
                  Routes with demand
                </span>
              </div>
              <p className="text-3xl font-extrabold text-foreground">
                {isSummaryLoading ? "..." : popularRoutes.length}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-accent">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">
                  Top waiting routes
                </span>
              </div>
              <ul className="space-y-1.5">
                {popularRoutes.map((r) => (
                  <li key={`${r.from}-${r.to}`} className="flex items-center justify-between text-xs">
                    <span className="truncate text-foreground">
                      {r.from} → {r.to}
                    </span>
                    <span className="ml-2 shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                      {r.count}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-accent">
                <Car className="h-5 w-5" />
                <span className="text-sm font-medium text-muted-foreground">
                  Seats needed
                </span>
              </div>
              <p className="text-3xl font-extrabold text-foreground">
                {isSummaryLoading ? "..." : summary?.seatsNeeded ?? 0}
              </p>
            </div>
          </div>
        </div>

        <MarketingRideSearchBar
          idPrefix="ride-requests"
          buttonClassName="bg-accent text-accent-foreground"
        />

        {errorMessage && (
          <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </p>
        )}

        <p className="mb-6 text-sm text-muted-foreground">
          Showing{" "}
          <strong className="text-foreground">{visibleGuestCount}</strong> guest
          requests for routes from <strong className="text-foreground">{departureLabel}</strong>
          {isRoutesLoading && " (loading…)"}
        </p>

        <div className="space-y-8">
          {routeSections.map((section) => (
            <section key={section.id}>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-bold text-foreground">
                  {section.from} → {section.to}
                </h2>
                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">
                  {section.waitingCount} waiting
                </span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {section.guests.map((guest) => (
                  <GuestCard key={guest.id} guest={guest} />
                ))}
              </div>
            </section>
          ))}
          {!isRoutesLoading && routeSections.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Search with from city, to city, and date to load waiting routes.
            </div>
          )}
        </div>

        <section className="mt-16">
          <h2 className="mb-2 text-xl font-bold text-foreground">
            🗺️ More demand across India
          </h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Popular corridors where travelers are waiting for hosts.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherRoutes.map((card) => (
              <div
                key={`${card.from}-${card.to}`}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">
                    {card.from} → {card.to}
                  </h3>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                    {card.count} waiting
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Total seats needed on this corridor: {card.totalSeatsNeeded}
                </p>
              </div>
            ))}
            {!isRoutesLoading && otherRoutes.length === 0 && (
              <p className="text-sm text-muted-foreground">No additional routes found.</p>
            )}
          </div>
        </section>

        <section className="mt-16 rounded-2xl border border-border bg-card p-8 sm:p-12">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            How Host Matching Works
          </h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "Browse demand",
                body: "Filter by your origin, destination, and dates to see who is waiting.",
              },
              {
                step: "2",
                title: "Offer a seat",
                body: "Send an offer from the Duri app when timing and price fit.",
              },
              {
                step: "3",
                title: "Confirm & travel",
                body: "Chat in-app, pick up verified travelers, and share fuel and tolls.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent text-lg font-bold text-accent-foreground shadow-sm">
                  {item.step}
                </div>
                <h3 className="mb-1 text-base font-bold text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12">
          <section className="rounded-2xl bg-guest-gradient p-8 sm:p-12 shadow-lg">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center text-primary-foreground">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-foreground/25">
                <Smartphone className="h-7 w-7" />
              </div>
              <h2 className="text-2xl font-bold sm:text-3xl">
                Host on Duri — Respond to Ride Requests
              </h2>
              <p className="text-primary-foreground/90">
                Download the app to see full profiles, message travelers, and publish your
                route when you&apos;re ready to drive.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg bg-primary-foreground px-5 py-3 text-sm font-semibold text-primary transition-opacity hover:opacity-90"
                >
                  Google Play <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border-2 border-primary-foreground/30 px-5 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  App Store <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="text-sm text-primary-foreground/85">
                Looking for a seat instead? See{" "}
                <Link
                  to={ROUTE_PATHS.FIND_RIDES}
                  className="font-semibold underline underline-offset-2"
                >
                  Available Rides
                </Link>
                .
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
